import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { tasksAPI } from '../../services/api';

const STATUS_COLORS = {
  'To Do':       { bg: '#f1f5f9', text: '#475569' },
  'In Progress': { bg: '#fef3c7', text: '#d97706' },
  'Review':      { bg: '#ede9fe', text: '#7c3aed' },
  'Done':        { bg: '#dcfce7', text: '#16a34a' },
  'Cancelled':   { bg: '#f3f4f6', text: '#6b7280' },
};

/**
 * DependencyPicker — searchable multi-select for task dependencies.
 *
 * Props:
 *   value          {Array}    current depends_on array: [{_id, task_number, title, status?, assigned_to?}]
 *   onChange       {Function} called with new array when selection changes
 *   excludeTaskId  {string}   ID of the current task (excluded from search results)
 *   circularError  {boolean}  show red warning banner (passed from parent after save error)
 *   readOnly       {boolean}  display-only mode (chips are clickable links, no search)
 */
export default function DependencyPicker({
  value = [],
  onChange,
  excludeTaskId,
  circularError = false,
  readOnly = false,
}) {
  const navigate = useNavigate();
  const [search,   setSearch]   = useState('');
  const [results,  setResults]  = useState([]);
  const [open,     setOpen]     = useState(false);
  const timerRef = useRef(null);
  const wrapRef  = useRef(null);

  // ── Debounced search ────────────────────────────────────────────────────
  useEffect(() => {
    if (!search.trim()) { setResults([]); setOpen(false); return; }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      tasksAPI.getTasks({ search: search.trim(), limit: 10 })
        .then(r => {
          const tasks = (r.data?.data?.tasks || []).filter(t => t._id !== excludeTaskId);
          setResults(tasks);
          setOpen(tasks.length > 0);
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [search, excludeTaskId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Toggle selection ────────────────────────────────────────────────────
  const toggle = (task) => {
    const exists = value.find(d => String(d._id) === String(task._id));
    if (exists) {
      onChange(value.filter(d => String(d._id) !== String(task._id)));
    } else {
      onChange([...value, {
        _id:         task._id,
        task_number: task.task_number,
        title:       task.title,
        status:      task.status,
        assigned_to: task.assigned_to,
      }]);
    }
    setSearch('');
    setResults([]);
    setOpen(false);
  };

  const remove = (dep) => {
    onChange(value.filter(d => String(d._id) !== String(dep._id)));
  };

  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 7,
    border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b',
    outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
    background: '#fff',
  };

  return (
    <div>
      {/* ── Circular dependency error banner ─────────────────────────────── */}
      {circularError && (
        <div style={{
          background: '#fee2e2', border: '1px solid #fecaca',
          color: '#dc2626', borderRadius: 8,
          padding: '8px 12px', marginBottom: 10, fontSize: 13, fontWeight: 600,
        }}>
          ⛔ Circular dependency detected. Cannot add this dependency.
        </div>
      )}

      {/* ── Dependency chips ─────────────────────────────────────────────── */}
      {value.length > 0 && (
        <div style={{ marginBottom: readOnly ? 0 : 8 }}>
          {/* Cancelled warnings */}
          {value.filter(d => d.status === 'Cancelled').map(dep => (
            <div key={`warn-${dep._id}`} style={{
              fontSize: 12, color: '#b45309',
              background: '#fffbeb', borderRadius: 6,
              padding: '5px 10px', marginBottom: 6,
              border: '1px solid #fde68a',
              display: 'flex', alignItems: 'center', gap: 5,
            }}>
              ⚠️ Dependency <strong>{dep.task_number}</strong> has been cancelled
            </div>
          ))}

          {/* Chips row */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {value.map(dep => {
              const sc = STATUS_COLORS[dep.status] || STATUS_COLORS['To Do'];
              return (
                <span
                  key={dep._id}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    background: '#fef3c7', color: '#92400e',
                    borderRadius: 5, padding: '3px 9px',
                    fontSize: 12, fontWeight: 600,
                    border: '1px solid #fde68a',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/tasks/${dep._id || dep}`)}
                    title={dep.title}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      cursor: 'pointer', color: '#92400e',
                      fontWeight: 600, fontSize: 12, fontFamily: 'monospace',
                    }}
                  >
                    {dep.task_number || '—'}
                  </button>
                  {dep.status && (
                    <span style={{
                      background: sc.bg, color: sc.text,
                      borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 600,
                    }}>
                      {dep.status}
                    </span>
                  )}
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => remove(dep)}
                      style={{
                        background: 'none', border: 'none',
                        cursor: 'pointer', padding: 0,
                        color: '#d97706', fontSize: 14, lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  )}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Search input (edit mode only) ─────────────────────────────────── */}
      {!readOnly && (
        <div ref={wrapRef} style={{ position: 'relative' }}>
          <input
            style={inputStyle}
            placeholder="Search task by number or title…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onFocus={() => results.length > 0 && setOpen(true)}
          />
          {open && results.length > 0 && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0,
              background: '#fff', border: '1px solid #e2e8f0',
              borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              zIndex: 20, maxHeight: 200, overflowY: 'auto', marginTop: 2,
            }}>
              {results.map(t => {
                const isSelected = value.find(d => String(d._id) === String(t._id));
                const sc = STATUS_COLORS[t.status] || STATUS_COLORS['To Do'];
                return (
                  <button
                    key={t._id}
                    type="button"
                    onClick={() => toggle(t)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      width: '100%', padding: '8px 12px',
                      border: 'none', borderBottom: '1px solid #f8fafc',
                      background: isSelected ? '#fef9ec' : '#fff',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    {isSelected && <span style={{ color: '#22c55e', fontSize: 13 }}>✓</span>}
                    <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace', flexShrink: 0 }}>
                      {t.task_number}
                    </span>
                    <span style={{ fontSize: 13, color: '#1e293b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.title}
                    </span>
                    <span style={{ background: sc.bg, color: sc.text, borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
                      {t.status}
                    </span>
                    {t.assigned_to?.name && (
                      <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0, maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.assigned_to.name}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Empty state for read-only mode */}
      {readOnly && value.length === 0 && (
        <span style={{ fontSize: 13, color: '#94a3b8' }}>None</span>
      )}
    </div>
  );
}
